import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { Navigate } from 'react-router-dom';
import { uploadHistory, originalImage, saveRemarkApi, deleteApi, deleteRemarkApi } from '../service/apiService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const HistoryPage = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const [activeSection, setActiveSection] = useState('history');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [modalImage, setModalImage] = useState(null);
  const [remarks, setRemarks] = useState({});
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const fetchImages = async () => {
        try {
          const response = await uploadHistory(currentPage, itemsPerPage, searchQuery);
          setImages(response.images);
          const initialRemarks = {};
          response.images.forEach((img) => {
            if (img.id) initialRemarks[img.id] = img.remark || '';
          });
          setRemarks(initialRemarks);
          setTotalPages(Math.ceil(response.total / itemsPerPage));
        } catch (error) {
          toast.error('Failed to fetch images');
        } finally {
          setLoading(false);
        }
      };
      fetchImages();
    }, 500); // debounce delay

    return () => clearTimeout(timeoutId); // Cleanup timeout
  }, [currentPage,itemsPerPage,searchQuery]);

  const handleSaveRemarks = async (id) => {
    try {
      const remark = remarks[id];
      const response = await saveRemarkApi(id, remark);
      if (!response.ok) {
        throw new Error('Failed to save remark');
      }
      setImages(prevImages =>
        prevImages.map(img =>
          img._id === id ? { ...img, remark } : img
        )
      );
      toast.success('Remark saved successfully!');
    } catch (error) {
      console.error(error);
      toast.error('Failed to save remark. Please try again.');
    } finally {
      setLoading(false);
    }
  };




  if (!token) {
    return <Navigate to="/login" />;
  }

  // Pagination logic
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prevPage => prevPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prevPage => prevPage - 1);
    }
  };

  // Function to open the modal
  const openModal = (imageSrc) => {
    setModalImage(imageSrc);
  };

  // Function to close the modal
  const closeModal = () => {
    setModalImage(null);
  };

  const deleteRecord = async (id) => {
    try {
      await deleteApi(id);

      setImages(prevImages => prevImages.filter(img => img._id !== id));
      toast.success('Record deleted successfully!');
    } catch (error) {
      console.error("Error while deleting record:", error);
      toast.error('Failed to delete record. Please try again.');
    }
  };

  const handleRemoveRemark = async (id) => {
    try {
      const response = await deleteRemarkApi(id);
      if (!response.ok) {
        throw new Error('Failed to remove remark');
      }
      setImages((prevImages) =>
        prevImages.map((img) =>
          img._id === id ? { ...img, remark: '' } : img
        )
      );
      setRemarks((prevRemarks) => {
        const newRemarks = { ...prevRemarks };
        delete newRemarks[id];
        return newRemarks;
      });

      toast.success('Remark removed successfully!');
    } catch (error) {
      console.error(error);
      toast.error('Failed to remove remark. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Sidebar activeSection={activeSection} setActiveSection={setActiveSection} />
      {loading ? (
        <div style={styles.loading}>Loading...</div>
      ) : (
        <div style={styles.container}>
          <div style={styles.topBar}>
            <h2 style={styles.heading}>History</h2>
            <div style={styles.searchWrapper}>
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.searchBar}
              />
            </div>
          </div>

          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Original</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Blip Caption</th>
                  <th style={styles.th}>Resnet50 Classification</th>
                  <th style={styles.th}>Yolov5 Detection</th>
                  <th style={styles.th}>Remark</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {images.length > 0 ? (
                  images.map((image, index) => (
                    <tr key={index} style={styles.tr}>
                      <td style={styles.td}>
                        {image.originalName ? (image.displayName ? image.displayName.split('/').pop() : image.originalName) : 'N/A'}
                      </td>
                      <td style={styles.td}>
                        <img
                          src={`${originalImage}${image.imagePath}`}
                          alt="Original"
                          style={styles.image}
                          onClick={() => openModal(`${originalImage}${image.imagePath}`)}
                        />
                      </td>
                      <td style={styles.td}>
                        {new Date(image.createdAt).toLocaleString()}
                      </td>
                      <td style={styles.td}>
                        {image.pythonOutput.blip_caption}
                      </td>
                      <td style={styles.td}>
                        <ul style={styles.pixelsList}>
                          {Array.isArray(image.pythonOutput.resnet50_classification) && image.pythonOutput.resnet50_classification.length > 0 ? (
                            image.pythonOutput.resnet50_classification.map((label, i) => (
                              <li key={i}>{label}</li>
                            ))
                          ) : (
                            <li style={styles.notProcessed}>Not available</li>
                          )}
                        </ul>

                      </td>
                      <td style={styles.td}>
                        <ul style={styles.calculationList}>
                          {Array.isArray(image.pythonOutput.yolov5_detections) && image.pythonOutput.yolov5_detections.length > 0 ? (
                            image.pythonOutput.yolov5_detections.map((det, i) => (
                              <li key={i}>{det}</li>
                            ))
                          ) : (
                            <li style={styles.notProcessed}>Not available</li>
                          )}
                        </ul>

                      </td>
                      <td style={styles.td}>
                        <div style={styles.remarkContainer}>
                          <textarea
                            id={`remark-${image._id}`}
                            value={remarks[image._id] || ''}
                            onChange={(e) =>
                              setRemarks((prevRemarks) => ({
                                ...prevRemarks,
                                [image._id]: e.target.value,
                              }))
                            }
                            style={{
                              ...styles.remarkInput,
                            }}
                          />
                          {remarks[image._id] && (
                            <div style={styles.buttonContainer}>
                              <button
                                onClick={() => handleSaveRemarks(image._id)}
                                style={{
                                  ...styles.saveButton,
                                }}
                              >
                                Save
                              </button>
                              <button
                                onClick={() => handleRemoveRemark(image._id)}
                                style={styles.removeButton}
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <button
                          onClick={() => deleteRecord(image._id)}
                          style={{
                            ...styles.deleteButton,
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" style={{ ...styles.td, textAlign: 'center', fontStyle: 'italic', color: '#888' }}>
                      No history available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={styles.pagination}>
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              style={currentPage === 1 ? { ...styles.pageButton, ...styles.pageButtonInactive } : styles.pageButton}
            >
              Previous
            </button>
            <span style={styles.pageNumber}>{`Page ${currentPage} of ${totalPages || 1}`}</span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages || totalPages === 0}
              style={currentPage === totalPages || totalPages === 0 ? { ...styles.pageButton, ...styles.pageButtonInactive } : styles.pageButton}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modal for fullscreen image */}
      {modalImage && (
        <div style={styles.modalOverlay} onClick={closeModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <img src={modalImage} alt="Full Screen" style={styles.modalImage} />
            {/* Close button */}
            <button onClick={closeModal} style={styles.closeButton}>Close</button>
          </div>
        </div>
      )}
    </>
  );
};



const styles = {
  container: {
    marginLeft: '275px',
    padding: '40px',
    backgroundColor: '#f4f6f9',
    fontFamily: 'Segoe UI, sans-serif',
    minHeight: '100vh',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '25px',
    flexWrap: 'wrap',
  },
  heading: {
    marginTop: '5px',
    fontSize: '26px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '10px',
  },
  loading: {
    textAlign: 'center',
    fontSize: '18px',
    marginTop: '100px',
    color: '#333',
  },
  tableWrapper: {
    overflowX: 'auto',
    backgroundColor: '#fff',
    borderRadius: '10px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '16px',
    textAlign: 'left',
    backgroundColor: '#00555A',
    fontWeight: '600',
    borderBottom: '2px solid #dee2e6',
    color: '#fff',
  },
  td: {
    padding: '20px',
    borderBottom: '1px solid #dee2e6',
    color: '#343a40',
    verticalAlign: 'top',
  },
  tr: {
    transition: 'background-color 0.3s',
  },
  image: {
    width: '70px',
    height: '70px',
    objectFit: 'cover',
    borderRadius: '6px',
    border: '1px solid #ddd',
    cursor: 'pointer',
  },
  notProcessed: {
    color: '#adb5bd',
    fontStyle: 'italic',
  },
  pagination: {
    textAlign: 'center',
    marginTop: '25px',
  },
  pageButton: {
    padding: '10px 16px',
    margin: '0 10px',
    backgroundColor: '#00555A',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.3s',
  },
  pageButtonInactive: {
    backgroundColor: '#b5b9c7',
    color: '#fff',
    cursor: 'not-allowed',
  },
  pageNumber: {
    fontSize: '16px',
    color: '#333',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    position: 'relative',
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '10px',
  },
  modalImage: {
    width: '100%',
    height: 'auto',
    maxWidth: '500px',
    background: "darkgray"
  },
  closeButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    backgroundColor: '#ff4d4d',
    color: '#fff',
    border: 'none',
    padding: '10px',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  
  pixelsList: {
    listStyleType: 'none',
    padding: 0,
    margin: 0,
    color: '#4a4a4a',
    lineHeight: '1.3',
    fontSize: '15px',
    fontWeight: '500',
    borderLeft: '3px solid #00555A',
    paddingLeft: '10px',
  },
  calculationList: {
    listStyleType: 'none',
    padding: 0,
    margin: 0,
    color: '#3a3a3a',
    lineHeight: '1.3',
    fontSize: '15px',
    fontWeight: '500',
    borderLeft: '3px solid #00555A',
    paddingLeft: '10px',

  },
  remarkContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    alignItems: 'flex-start',
    marginTop: '8px',
  },

  remarkInput: {
    padding: '10px 14px',
    fontSize: '14px',
    borderRadius: '6px',
    border: '1px solid #ced4da',
    width: '100%',
    maxWidth: '100px',
    backgroundColor: '#fdfdff',
    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)',
  },

  buttonContainer: {
    display: 'flex',
    gap: '6px',
    marginTop: '8px',
  },

  saveButton: {
    padding: '8px 16px',
    backgroundColor: 'rgb(0, 85, 90',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    transition: 'background-color 0.3s',
  },

  saveButtonDisabled: {
    backgroundColor: '#d6d6d6',
    cursor: 'not-allowed',
    color: '#888',
  },

  removeButton: {
    padding: '8px 16px',
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    transition: 'background-color 0.3s, transform 0.2s',
  },

  removeButtonHover: {
    backgroundColor: '#b5b5b5',
  },

  removeButtonActive: {
    transform: 'scale(0.98)',
  },
  toast: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    padding: '10px 20px',
    borderRadius: '6px',
    color: '#fff',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 1001,
  },

  toastCloseButton: {
    backgroundColor: 'transparent',
    color: '#fff',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
  },
  deleteButton: {
    padding: '12px 25px',
    backgroundColor: 'rgb(0, 85, 90',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    transition: 'background-color 0.3s, transform 0.2s',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    marginTop: '10px'
  },

  deleteButtonHover: {
    backgroundColor: 'rgb(18, 48, 50)',
  },

  deleteButtonActive: {
    transform: 'scale(0.98)',
  },
  deleteButtonDisabled: {
    backgroundColor: '#d6d6d6',
    color: '#888',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  remarkInputDisabled: {
    backgroundColor: '#d6d6d6',
    color: '#888',
    borderColor: '#ccc',
    cursor: 'not-allowed',
    resize: 'none',
  },
  searchWrapper: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '6px',
    padding: '5px 10px',
    maxWidth: '300px',
    width: '100%',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },

  searchBar: {
    border: 'none',
    outline: 'none',
    fontSize: '15px',
    flex: 1,
  },


};

export default HistoryPage;

